/**
 * Ryan O'Dowd
 * 2018-06-09
 * © Copyright 2018 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
} from 'react';
import AuthButton from '@containers/Auth/AuthButton';
import {
  GoogleOneTapSignIn,
} from '@react-native-google-signin/google-signin';
import {
  Platform,
} from 'react-native';
import fb from '@utils/firebase';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const GoogleAuthButton = (props: GoogleAuthButtonProps) => {
  const navigation = hooks.useNavigation();
  const i18n = hooks.useI18n(props.localeId);

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    GoogleOneTapSignIn.configure({
      webClientId: globals.webClientId,
    });

    const checkPlayServices = async () => {
      await GoogleOneTapSignIn.checkPlayServices();
    };
    checkPlayServices();
  }, []);

  const _onResponse = (response) => {
    if (response.type === 'success') {
      const credential = fb.GoogleAuthProvider.credential(response.data.idToken);
      if (props.isLinking) {
        utilities.linkUserAndCredential(fb.auth.currentUser, credential, navigation);
      } else {
        fb.signInWithCredential(fb.auth, credential);
        fb.logEvent(fb.analytics, 'login', {
          displayName: response.data.user.name,
          authType: 'google',
        });
      }
    } else {
      // user canceled sign in
      props.setAuthIsPending(false);
    }
  };

  const _onError = (error) => {
    switch (error.code) {
      case 'auth/network-request-failed': {
        props.setErrorMessage(i18n('networkError'));
        break;
      }
      case 'auth/provider-already-linked': {
        props.setErrorMessage(i18n('alreadyLinkedError'));
        break;
      }
      case 'auth/popup-closed-by-user': {
        break;
      }
      default: {
        fb.recordError(fb.crashlytics, error);
      }
    }

    props.setAuthIsPending(false);
  };

  return (
    <AuthButton
      iconName='google'
      providerName='Google'
      localeId={props.localeId}
      isLinking={props.isLinking}
      authIsPending={props.authIsPending}
      setAuthIsPending={props.setAuthIsPending}
      onPress={async () => {
        if (Platform.OS === 'web') {
          const provider = new fb.GoogleAuthProvider;
          provider.setCustomParameters({
            prompt: 'select_account', // forces the account chooser
          });
          fb.signInWithPopup(fb.auth, provider).then((response) => {
            _onResponse(response);
          }).catch((error) => {
            _onError(error);
          });
        } else {
          // this is native
          try {
            const signInResponse = await GoogleOneTapSignIn.presentExplicitSignIn();
            _onResponse(signInResponse);
          } catch (error) {
            _onError(error);
          }
        }
      }}
    />
  );
};

class GoogleAuthButtonProps {
  setErrorMessage: (errorMessage: string) => void;
  localeId: any;
  setAuthIsPending: (authIsPending: boolean) => void;

  authIsPending?: boolean;
  isLinking?: boolean;
};

export default GoogleAuthButton;
